/**
 * The audit trail's filters, as the reader sets them and as the server reads them.
 *
 * @module components/admin/audit-trail-filters
 */

import type { AuditQuery } from "@/platform";
import type { AuditEventType } from "@/server/audit-events";

/** What the filter controls hold: ids, an exact action, and local calendar days. */
export interface AuditFilterValues {
  actor: string;
  action: string;
  programmeId: string;
  /** `YYYY-MM-DD`, the first local day shown. */
  from: string;
  /** `YYYY-MM-DD`, the last local day shown. */
  to: string;
}

export const NO_AUDIT_FILTERS: AuditFilterValues = {
  actor: "",
  action: "",
  programmeId: "",
  from: "",
  to: "",
};

/**
 * Actions the live platform records, for the action filter. The server
 * matches exactly, so an action missing here cannot be picked but still
 * renders in the trail.
 */
export const AUDIT_ACTIONS: readonly AuditEventType[] = [
  "programme.created",
  "programme.updated",
  "programme.workflow-transitioned",
  "workflow.transitioned",
  "programme.readiness-overridden",
  "programme.returned-to-draft",
  "programme.review-round-opened",
  "programme.review-recorded",
  "programme.approval-candidate-created",
  "programme.approved",
  "programme.exported",
  "programme.delivery-status-changed",
  "programme.end-of-life-set",
  "programme.publication.published",
  "programme.publication.withdrawn",
  "programme-responsibility.assigned",
  "programme-responsibility.revoked",
  "final-sign-off.recorded",
  "module.created",
  "module.saved",
  "module.submitted",
  "module.reviewed",
  "module-owner.assigned",
  "user.invited",
  "user.activated",
  "user.deactivated",
  "user.email-changed",
  "invitation.created",
  "invitation.reissued",
  "role.changed",
  "tenant-capabilities.changed",
  "settings.provider.updated",
  "settings.access-policy.updated",
  "impersonation.started",
  "impersonation.stopped",
  "login.success",
  "edit-lease.acquired",
  "edit-lease.released",
];

/** Midnight at the start of a local calendar day, as an ISO instant. */
function localDayStart(day: string, offsetDays = 0): string {
  const [year, month, date] = day.split("-").map(Number);
  return new Date(year ?? 0, (month ?? 1) - 1, (date ?? 1) + offsetDays).toISOString();
}

/** True when both days are set and the range runs backwards. */
export function auditRangeBackwards(values: AuditFilterValues): boolean {
  return Boolean(values.from && values.to && values.from > values.to);
}

/**
 * The server query for what the controls hold. `to` covers its whole day, so
 * the bound sent is the next day's start, which the server treats as exclusive.
 * A backwards range sends no dates rather than a query the server refuses.
 */
export function auditQueryFor(values: AuditFilterValues): Omit<AuditQuery, "limit"> {
  const dated = !auditRangeBackwards(values);
  return {
    ...(values.actor ? { actor: values.actor } : {}),
    ...(values.action ? { action: values.action } : {}),
    ...(values.programmeId ? { programmeId: values.programmeId } : {}),
    ...(dated && values.from ? { since: localDayStart(values.from) } : {}),
    ...(dated && values.to ? { until: localDayStart(values.to, 1) } : {}),
  };
}

/** Whether any control narrows the trail. */
export function auditFiltersActive(values: AuditFilterValues): boolean {
  return Object.values(values).some(Boolean);
}
